import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent } from "./components/card.js";
import { Link } from "react-router-dom";
import AuthModal from "../modals/AuthModal.js";
import BookingModal from "../Patient/BookingModal.js";
import RoleModal from "../modals/RoleModal.js";
import NpiSearchModal from "../modals/NpiSearchModal.js";
import ProviderPreviewModal from "../modals/ProviderPreviewModal.js";
import ProviderVerificationDocumentModal from "../modals/ProviderVerificationDocumentModal.js";
import {
  filterProviders,
  getTodayInZone,
  claimProviderByNpi,
} from "../APIs/providerApi.js";
import { getSpecialties, getInsurancePlans } from "../APIs/dropdownApi.js";
import { useNavigate } from "react-router-dom";
import {
  getProviderAvailabilityToday,
  getFreeStartTimes,
} from "../APIs/availability.js";
import { getProviderRatings } from "../APIs/ratingsApi.js";
import {
  Bell,
  Search,
  SlidersHorizontal,
  UserCircle,
  Stethoscope,
  MapPin,
  Globe,
  User,
  Clock,
  CheckCircle,
  X,
} from "lucide-react";
import usStates from "../data/us-states.json";
import { getStates, getCitiesByState } from "../APIs/geoApi";
import keycloak from "../APIs/keycloak.js";
import Header from "../modals/Header.js";
import AdvancedFiltersModal from "./components/AdvancedFiltersModal.js";
import { useCurrentUser } from "../APIs/UserContext.js";
import { showToast } from "../APIs/utils/toastManager.js";
import { useBackendReady } from "../APIs/BackendReadyContext";

function ProviderSearchPage() {
  const backendReady = useBackendReady();
  //For persisting login
  const { currentUser, loading: userLoading, refreshUser } = useCurrentUser();
  const [, setDropdownOpen] = useState(false);
  const dropdownRef = useRef();
  const [searchTerm, setSearchTerm] = useState("");

  // Derive patientId from currentUser
  const patientId = currentUser?.patientId || null;

  // Dropdown data from BE
  const [specialties, setSpecialties] = useState([]);
  const [insurances, setInsurances] = useState([]);

  //To close panel
  const [isAvailBetweenOpen, setIsAvailBetweenOpen] = useState(false);

  //Load current user and persist patient to DB
  //const [currentUser, setCurrentUser] = useState(null);
  //const [userLoading, setUserLoading] = useState(true);

  const [showRoleModal, setShowRoleModal] = useState(false);
  const openRoleModal = () => setShowRoleModal(true);
  const closeRoleModal = () => setShowRoleModal(false);

  //modal in flow (provider)
  const [showNpiModal, setShowNpiModal] = useState(false);
  const [providerPreview, setProviderPreview] = useState(null);
  const [pendingNpi, setPendingNpi] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  const handleBackToNpi = () => {
    setShowPreviewModal(false);
    setShowNpiModal(true);
  };

  //For Logged user
  const loggedInEmail =
    currentUser?.email || keycloak.tokenParsed?.email || null;

  //fetchCurrent User
  /*useEffect(() => {
    let isMounted = true;

    async function bootstrapUser() {
      // If not authenticated, nothing to do
      if (!keycloak || !keycloak.authenticated) {
        if (isMounted) {
          setUserLoading(false);
        }
        return;
      }

      try {
        const user = await fetchCurrentUser(); // this will also call minimal-signup
        if (!isMounted) return;
        setCurrentUser(user || null);
      } catch (err) {
        console.error(
          "Error bootstrapping current user on ProviderSearchPage:",
          err,
        );
        if (isMounted) {
          setUserError("Unable to load your account info.");
        }
      } finally {
        if (isMounted) {
          setUserLoading(false);
        }
      }
    }

    bootstrapUser();

    return () => {
      isMounted = false;
    };
  }, []);*/

  //Ratings
  const [ratingsMap, setRatingsMap] = useState({}); // { [id]: { averageRating, reviewCount } }

  //Dynamic city and state sort
  const [stateOptions, setStateOptions] = useState([]); // [{value,label,code}]
  const [cityOptions, setCityOptions] = useState([]); // [{value,label}]
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // local selection (don’t add ids to your API filters)
  const [selectedStateId, setSelectedStateId] = useState("");
  const [selectedCityId, setSelectedCityId] = useState("");

  //Prevent double-clicks during async preference check

  //Opening up sign In and SignUp modal
  const [showAuthModal, setShowAuthModal] = useState(false);
  const openAuthModal = () => setShowAuthModal(true);
  const closeAuthModal = () => setShowAuthModal(false);

  // Inline booking modal state
  const [bookingSlot, setBookingSlot] = useState(null);

  //Gating for Provider Alert
  const handleCreateProviderAlertClick = () => {
    // While we're still figuring out the user, do nothing
    if (userLoading) return;

    // If no logged-in email, open the auth modal instead of navigating
    if (!loggedInEmail) {
      openAuthModal();
      return;
    }

    // Authenticated → allow navigation to CreateProviderAlert
    navigate("/CreateProviderAlert");
  };

  const handleSignIn = () => {
    if (!backendReady) {
      showToast("MedMaze is loading, please wait.", "info");
      return;
    }

    // Clear stale keycloak callback entries from previous login attempts
    Object.keys(localStorage)
      .filter((k) => k.startsWith("kc-callback-"))
      .forEach((k) => localStorage.removeItem(k));

    keycloak.login({
      redirectUri: window.location.origin + "/ProviderSearchPage",
    });
  };

  //Search by Npi
  const handleFoundByNpi = ({ preview, npi }) => {
    setProviderPreview(preview);
    setPendingNpi(npi);
    setShowNpiModal(false);
    setShowPreviewModal(true);
  };

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Title-case a name string: "JOHN DOE" → "John Doe"
  const toTitleCase = (str = "") =>
    str.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

  // Ordinal suffix: 1 → "1st", 3 → "3rd", 24 → "24th"
  const ordinalSuffix = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  // Renders unified availability section for a provider card
  const renderAvailability = (provider) => {
    const { earliestAvailability } = provider;

    if (!earliestAvailability) {
      return (
        <p className="text-sm text-gray-400 italic">
          Check back for availability
        </p>
      );
    }

    const todayStr = getTodayInZone("America/New_York"); // "YYYY-MM-DD"
    const dateObj = new Date(earliestAvailability);

    // Format earliestAvailability to "YYYY-MM-DD" in ET for comparison
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(dateObj);
    const pMap = Object.fromEntries(parts.map((p) => [p.type, p.value]));
    const earliestFormatted = `${pMap.year}-${pMap.month}-${pMap.day}`;
    const isToday = earliestFormatted === todayStr;

    if (isToday) {
      const slots = todayAvailabilities[provider.id];
      return (
        <div>
          <p className="text-sm font-medium text-gray-700 mb-1">
            Earliest Available: Today
          </p>
          {slots && slots.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {slots.map((zdt, idx) => (
                <button
                  key={`${zdt}-${idx}`}
                  className="px-3 py-1.5 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700"
                  title={zdt}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (!currentUser) {
                      openAuthModal();
                      return;
                    }
                    setBookingSlot({
                      providerId: provider.id,
                      zdt,
                      providerName: provider.name,
                      specialty: provider.specialty,
                      facility: provider.facility,
                    });
                  }}
                >
                  {fmtInZone(zdt, extractZone(zdt))}
                </button>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">
              Check back for availability
            </p>
          )}
        </div>
      );
    }

    // Future date: format as "April 24th"
    const monthName = dateObj.toLocaleString("en-US", {
      month: "long",
      timeZone: "America/New_York",
    });
    const dayNum = parseInt(
      dateObj.toLocaleString("en-US", {
        day: "numeric",
        timeZone: "America/New_York",
      }),
      10,
    );

    // Future date — show slots fetched for that specific date
    const slots = earliestDateAvailabilities[provider.id];
    return (
      <div>
        <p className="text-sm font-medium text-gray-700 mb-1">
          Earliest Available: {monthName} {ordinalSuffix(dayNum)}
        </p>
        {slots && slots.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {slots.map((zdt, idx) => (
              <button
                key={`${zdt}-${idx}`}
                className="px-3 py-1.5 rounded-lg text-sm bg-blue-600 text-white hover:bg-blue-700"
                title={zdt}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!currentUser) {
                    openAuthModal();
                    return;
                  }
                  setBookingSlot({
                    providerId: provider.id,
                    zdt,
                    providerName: provider.name,
                    specialty: provider.specialty,
                    facility: provider.facility,
                  });
                }}
              >
                {fmtInZone(zdt, extractZone(zdt))}
              </button>
            ))}
          </div>
        ) : slots === undefined ? (
          <p className="text-sm text-gray-400 italic">Loading slots…</p>
        ) : (
          <p className="text-sm text-gray-400 italic">
            Check back for availability
          </p>
        )}
      </div>
    );
  };

  //For availabilities
  const navigate = useNavigate();
  const [todayAvailabilities, setTodayAvailabilities] = useState({});
  const [earliestDateAvailabilities, setEarliestDateAvailabilities] = useState(
    {},
  );

  // Extract IANA timezone from a ZDT string like "...T16:30:00-04:00[America/New_York]"
  const extractZone = (zdt) => {
    const m = String(zdt).match(/\[([^\]]+)\]/);
    return m ? m[1] : "America/New_York";
  };

  // Helper for local time formatting
  const fmtInZone = (isoStr, zone = "America/New_York") => {
    try {
      return new Date(isoStr).toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: zone,
      });
    } catch {
      return isoStr;
    }
  };

  // Default filter shape — keys match backend (experience handled client-side)
  const defaultFilters = {
    specialtyName: null,
    planName: null,
    languageName: null,
    gender: null,
    ethnicity: null,
    city: null,
    state: null,
    availabilityDate: null,
    availabilityFrom: null,
    availabilityTo: null,
    availabilityZone: "America/New_York",
    availabilityTelehealth: null,
    acceptingNewPatients: null,
    zipCode: null,
    centerLatitude: null,
    centerLongitude: null,
    radiusInMiles: 25,
    locationLabel: "",
  };

  // appliedFilters triggers API; stagedFilters is what the modal edits
  const [appliedFilters, setAppliedFilters] = useState({ ...defaultFilters });
  const [stagedFilters, setStagedFilters] = useState({ ...defaultFilters });
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [showLocationBanner, setShowLocationBanner] = useState(
    () => !localStorage.getItem("medmaze_location_banner_dismissed"),
  );

  const handleEnableLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const locUpdate = {
          centerLatitude: coords.latitude,
          centerLongitude: coords.longitude,
          radiusInMiles: 25,
          locationLabel: "Current Location",
        };
        setStagedFilters((prev) => ({ ...prev, ...locUpdate }));
        setAppliedFilters((prev) => ({ ...prev, ...locUpdate }));
        setCurrentPage(0);
        setShowLocationBanner(false);
        localStorage.setItem("medmaze_location_banner_dismissed", "1");
      },
      () => {
        setShowLocationBanner(false);
        localStorage.setItem("medmaze_location_banner_dismissed", "1");
      },
    );
  };

  // Experience range — client-side only, not sent to backend
  const [minYearsOfExperience, setMinYearsOfExperience] = useState(null);
  const [maxYearsOfExperience, setMaxYearsOfExperience] = useState(null);

  const [providers, setProviders] = useState([]);

  //Dynamic State select
  useEffect(() => {
    let on = true;
    (async () => {
      setLoadingStates(true);
      try {
        const states = await getStates(); // [{id, name, stateCode}]
        if (!on) return;
        setStateOptions(
          (states || []).map((s) => ({
            value: String(s.id),
            label: s.name,
            code:
              s.stateCode ||
              (usStates.find((u) => u.name === s.name)?.code ?? ""),
          })),
        );
      } finally {
        if (on) setLoadingStates(false);
      }
    })();
    return () => {
      on = false;
    };
  }, []);

  //Dynamic city select
  useEffect(() => {
    if (!selectedStateId) {
      setCityOptions([]);
      setSelectedCityId("");
      // clear staged city too when state clears
      setStagedFilters((prev) => ({ ...prev, city: null, state: null }));
      return;
    }

    let on = true;
    (async () => {
      setLoadingCities(true);
      try {
        const cities = await getCitiesByState(Number(selectedStateId)); // [{id, name}]
        if (!on) return;
        setCityOptions(
          (cities || []).map((c) => ({ value: String(c.id), label: c.name })),
        );
      } finally {
        if (on) setLoadingCities(false);
      }
    })();

    return () => {
      on = false;
    };
  }, [selectedStateId]);

  // 🔹 Fetch Specialties and Insurances
  useEffect(() => {
    getSpecialties().then(setSpecialties);
    getInsurancePlans().then(setInsurances);
  }, []);

  // 🔹 Debounce searchTerm → debouncedSearch (400ms); only resets page when value actually changes
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch((prev) => {
        if (prev !== searchTerm) setCurrentPage(0);
        return searchTerm;
      });
    }, 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  //For provider availabilities
  useEffect(() => {
    const fetchTodayAvail = async () => {
      const results = {};
      for (const p of providers) {
        try {
          const freeStarts = await getProviderAvailabilityToday(p.id);
          results[p.id] = Array.isArray(freeStarts) ? freeStarts : [];
        } catch (err) {
          results[p.id] = [];
        }
      }
      setTodayAvailabilities(results);
    };
    if (providers.length > 0) fetchTodayAvail();
  }, [providers]);

  // Fetch free slots for each provider's earliest future availability date
  useEffect(() => {
    const fetchEarliestDateSlots = async () => {
      const todayStr = getTodayInZone("America/New_York");
      const results = {};

      for (const p of providers) {
        if (!p.earliestAvailability) continue;

        // Derive YYYY-MM-DD in ET
        const dateObj = new Date(p.earliestAvailability);
        const parts = new Intl.DateTimeFormat("en-US", {
          timeZone: "America/New_York",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }).formatToParts(dateObj);
        const pMap = Object.fromEntries(parts.map((pt) => [pt.type, pt.value]));
        const earliestDateStr = `${pMap.year}-${pMap.month}-${pMap.day}`;

        // Skip today — already covered by todayAvailabilities
        if (earliestDateStr === todayStr) continue;

        try {
          const slots = await getFreeStartTimes(p.id, {
            date: earliestDateStr,
            zone: "America/New_York",
          });
          results[p.id] = Array.isArray(slots) ? slots : [];
        } catch (err) {
          results[p.id] = [];
        }
      }
      setEarliestDateAvailabilities(results);
    };

    if (providers.length > 0) fetchEarliestDateSlots();
  }, [providers]);

  //Provider Ratings
  useEffect(() => {
    let cancelled = false;
    const fill = async () => {
      const ids = providers.map((p) => p.id).filter(Boolean);
      const next = {};
      for (const id of ids) {
        // skip if BE already gave rating on the provider
        const already = providers.find((p) => p.id === id)?.averageRating;
        if (already != null) {
          next[id] = {
            averageRating: already,
            reviewCount: providers.find((p) => p.id === id)?.reviewCount ?? 0,
          };
          continue;
        }
        try {
          const r = await getProviderRatings(id);
          next[id] = {
            averageRating: r.averageRating ?? null,
            reviewCount: r.reviewCount ?? 0,
          };
        } catch {
          /* ignore */
        }
      }
      if (!cancelled) setRatingsMap(next);
    };
    if (providers.length) fill();
    return () => {
      cancelled = true;
    };
  }, [providers]);

  // 🔹 Helper: join safely
  const joinComma = (arr) =>
    Array.isArray(arr) && arr.length ? arr.join(", ") : null;

  const pickFacility = (p) => {
    // filter response: facilityName
    if (p?.facilityName) return p.facilityName;

    // unfiltered/all-providers: facilityInformation[]
    if (Array.isArray(p?.facilityInformation) && p.facilityInformation.length) {
      const names = p.facilityInformation
        .map((f) => f?.facilityName)
        .filter(Boolean);
      if (names.length) return names.join(", ");
    }
    return "N/A";
  };

  const pickLocation = (p) => {
    // filter response: locations: ["Atlanta, GA"]
    const byLocations = joinComma(p?.locations);
    if (byLocations) return byLocations;

    // some payloads: facilityInformation[] with { city, state }
    if (Array.isArray(p?.facilityInformation) && p.facilityInformation.length) {
      const locs = p.facilityInformation
        .map((f) => [f?.city, f?.state].filter(Boolean).join(", "))
        .filter(Boolean);
      if (locs.length) return locs.join("; ");
    }

    // other payloads: facilityLocations[] with { city, state }
    if (Array.isArray(p?.facilityLocations) && p.facilityLocations.length) {
      const locs = p.facilityLocations
        .map((l) => [l?.city, l?.state].filter(Boolean).join(", "))
        .filter(Boolean);
      if (locs.length) return locs.join("; ");
    }

    return "N/A";
  };

  //NEW TO NORMALIZE LANGUAGE
  const normalizeLanguages = (langs) => {
    if (!Array.isArray(langs) || langs.length === 0) return "N/A";

    const list = (
      typeof langs[0] === "string" ? langs : langs.map((l) => l?.languageName)
    )
      .map((s) => (s ?? "").trim())
      .filter(Boolean);

    if (list.length === 0) return "N/A";
    return list.sort((a, b) => a.localeCompare(b)).join(", ");
  };

  // add this helper next to Normalize insurance
  const normalizeInsurances = (ins) => {
    if (!Array.isArray(ins) || ins.length === 0) return "N/A";
    // supports both shapes: ["Aetna", "Cigna"] OR [{planName:"Aetna"}, {planName:"Cigna"}]
    const list = typeof ins[0] === "string" ? ins : ins.map((i) => i?.planName);
    const names = list.map((s) => (s ?? "").trim()).filter(Boolean);
    return names.length
      ? names.sort((a, b) => a.localeCompare(b)).join(", ")
      : "N/A";
  };

  //TO FIX LANGUAGE FLIP
  /*const formatProviders = (data = []) =>
    data.map((p) => ({
      id: p.providerId,
      name: `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim(),
      specialty:
        p.specialty ??
        (Array.isArray(p.specialties) && p.specialties.length
          ? p.specialties
              .map((s) => s?.name)
              .filter(Boolean)
              .join(", ")
          : "N/A"),
      facilities: facilitiesOf(p),
      facility: pickFacility(p),
      location: pickLocation(p),

      // ✅ deterministic, error-proof languages
      languages: normalizeLanguages(p.languages),

      insurance:
        Array.isArray(p.insurances) && p.insurances.length
          ? p.insurances
              .map((i) => i?.planName)
              .filter(Boolean)
              .join(", ")
          : "N/A",
      insurance: normalizeInsurances(p.insurances),
      ethnicity: p.ethnicity || "N/A",
      gender: p.gender || "N/A",
      yearsExperience: p.yearsOfExperience ?? "N/A",
      averageRating: p.averageRating ?? null,
      reviewCount: p.reviewCount ?? null,
    }));*/

  //TO FIX LANGUAGE FLIP
  const formatProviders = (data = []) => {
    const formatted = data.map((p) => ({
      id: p.providerId,
      name: `${p.firstName ?? ""} ${p.lastName ?? ""}`.trim(),
      specialty:
        p.specialty ??
        (Array.isArray(p.specialties) && p.specialties.length
          ? p.specialties
              .map((s) => s?.name)
              .filter(Boolean)
              .join(", ")
          : "N/A"),
      facilities: facilitiesOf(p),
      facility: pickFacility(p),
      location: pickLocation(p),
      languages: normalizeLanguages(p.languages),
      insurance: normalizeInsurances(p.insurances),
      ethnicity: p.ethnicity || "N/A",
      gender: p.gender || "N/A",
      yearsExperience: p.yearsOfExperience ?? "N/A",
      averageRating: p.averageRating ?? null,
      reviewCount: p.reviewCount ?? null,
      earliestAvailability: p.earliestAvailability ?? null,
      acceptingNewPatients: p.acceptingNewPatients ?? false,
      profilePictureUrl: p.profilePictureUrl ?? null,
      distanceMiles: p.distanceMiles ?? null,
    }));

    // ✅ Filter out providers with N/A name or specialty
    return formatted.filter(
      (provider) =>
        provider.name && provider.name !== "" && provider.specialty !== "N/A",
    );
  };

  // 🔹 Unified fetch — runs on mount, page change, filter apply, or search change
  useEffect(() => {
    if (!backendReady) return;
    const fetchProviders = async () => {
      setProviders([]);
      try {
        // eslint-disable-next-line no-unused-vars
        const { locationLabel, ...rest } = appliedFilters;
        const filters = { ...rest, nameSearch: debouncedSearch || null };
        const data = await filterProviders(filters, currentPage, 20);
        setProviders(formatProviders(data.content));
        setTotalPages(data.totalPages);
      } catch (error) {
        console.error("Error fetching providers:", error);
      }
    };
    fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendReady, appliedFilters, currentPage, debouncedSearch]);

  // 🔹 Apply staged filters → triggers API call
  const handleApplyFilters = () => {
    setCurrentPage(0);
    setAppliedFilters({ ...stagedFilters });
    setFiltersOpen(false);
  };

  // 🔹 Reset all filters to defaults
  const handleResetFilters = () => {
    setStagedFilters({ ...defaultFilters });
    setAppliedFilters({ ...defaultFilters });
    setSelectedStateId("");
    setSelectedCityId("");
    setMinYearsOfExperience(null);
    setMaxYearsOfExperience(null);
    setFiltersOpen(false);
  };

  // 🔹 Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // providers is already filtered + sorted by the backend
  const filteredProviders = providers;

  // Convert facility arrays to a unified shape
  const facilitiesOf = (p) => {
    const src = Array.isArray(p?.facilityInformation)
      ? p.facilityInformation
      : Array.isArray(p?.facilityLocations)
        ? p.facilityLocations
        : [];
    return src
      .map((f) => ({
        name: f?.facilityName,
        addressLine: f?.addressLine,
        city: f?.city,
        state: f?.state,
      }))
      .filter((x) => x.name && x.addressLine && x.city && x.state);
  };

  // Build a Google Maps directions link
  /*const mapsLink = ({ addressLine, city, state }) =>
      `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${addressLine}, ${city}, ${state}`
      )}`;*/

  const LOCKED_KEY = "medmaze_signup_role_locked";
  const PENDING_KEY = "medmaze_signup_role_pending";
  const PENDING_CLAIM_KEY = "medmaze_pending_claim_npi";

  const lockRoleAndRegister = (role) => {
    try {
      localStorage.setItem(LOCKED_KEY, role); // lock ONLY here
      localStorage.removeItem(PENDING_KEY); // cleanup
    } catch {}

    keycloak.register({ redirectUri: window.location.origin });
  };

  const handleSelectRole = (role) => {
    closeRoleModal();

    if (role === "PATIENT") {
      lockRoleAndRegister("PATIENT");
      return;
    }

    // PROVIDER goes to NPI flow (no lock yet)
    setShowNpiModal(true);
  };

  return (
    <>
      {/* ✅ Global MedMaze Header */}
      <Header onOpenSignUp={openRoleModal} />

      {/* Hero */}
      <section className="text-center py-16 px-4 bg-white">
        <h1 className="text-5xl font-bold text-gray-900">
          Find your <span className="text-teal-600">perfect provider</span>
        </h1>
        <p className="mt-4 text-lg text-gray-500 max-w-xl mx-auto">
          Browse our network of compassionate healthcare professionals and find
          the right match for your needs.
        </p>
      </section>

      <div className="max-w-7xl mx-auto px-6 pb-8 space-y-6">
        {/* Search bar row */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Search input */}
          <div className="relative w-full md:flex-1">
            <Search
              size={18}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search by Name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 rounded-full border border-gray-300 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            />
          </div>

          {/* Radius dropdown */}
          <div className="relative">
            <select
              value={appliedFilters.radiusInMiles || 25}
              onChange={(e) => {
                const r = Number(e.target.value);
                setStagedFilters((prev) => ({ ...prev, radiusInMiles: r }));

                if (
                  appliedFilters.centerLatitude &&
                  appliedFilters.centerLongitude
                ) {
                  // Location already set — just update radius and refetch
                  setAppliedFilters((prev) => ({ ...prev, radiusInMiles: r }));
                  setCurrentPage(0);
                } else if (navigator.geolocation) {
                  // No location yet — request it now so distance search activates
                  navigator.geolocation.getCurrentPosition(
                    ({ coords }) => {
                      const locUpdate = {
                        centerLatitude: coords.latitude,
                        centerLongitude: coords.longitude,
                        radiusInMiles: r,
                        locationLabel: "Current Location",
                      };
                      setStagedFilters((prev) => ({ ...prev, ...locUpdate }));
                      setAppliedFilters((prev) => ({ ...prev, ...locUpdate }));
                      setCurrentPage(0);
                      setShowLocationBanner(false);
                      localStorage.setItem(
                        "medmaze_location_banner_dismissed",
                        "1",
                      );
                    },
                    () => {
                      // Denied — just update radius without distance search
                      setAppliedFilters((prev) => ({
                        ...prev,
                        radiusInMiles: r,
                      }));
                      setCurrentPage(0);
                    },
                  );
                } else {
                  setAppliedFilters((prev) => ({ ...prev, radiusInMiles: r }));
                  setCurrentPage(0);
                }
              }}
              className={`appearance-none pl-4 pr-8 py-3 rounded-full border font-medium text-sm cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500
                ${
                  appliedFilters.centerLatitude
                    ? "border-teal-500 bg-teal-50 text-teal-700"
                    : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                }`}
            >
              <option value={25}>Radius: 25 miles</option>
              <option value={10}>Radius: 10 miles</option>
              <option value={5}>Radius: 5 miles</option>
            </select>
            <svg
              className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.24a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z" />
            </svg>
          </div>

          {/* Filters button */}
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="flex items-center gap-2 px-5 py-3 rounded-full border border-gray-300 bg-white text-gray-700 font-medium text-sm hover:bg-gray-50 transition-colors"
          >
            <SlidersHorizontal size={16} />
            Filters
          </button>

          {/* Bell */}
          <button
            type="button"
            title="Create a Provider alert"
            aria-label="Create a Provider alert"
            onClick={handleCreateProviderAlertClick}
            className="p-3 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-yellow-400"
          >
            <Bell
              size={20}
              className="text-yellow-500 hover:text-yellow-600 transition-colors"
              aria-hidden="true"
            />
          </button>
        </div>

        {/* Location permission banner */}
        {showLocationBanner && (
          <div className="flex items-center justify-between px-4 py-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
            <span className="flex items-center gap-2">
              <MapPin size={16} className="text-blue-600 flex-shrink-0" />
              Allow location access to find providers near you.
            </span>
            <div className="flex items-center gap-2 ml-4 flex-shrink-0">
              <button
                type="button"
                onClick={handleEnableLocation}
                className="px-3 py-1 rounded-full bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700"
              >
                Enable
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowLocationBanner(false);
                  localStorage.setItem(
                    "medmaze_location_banner_dismissed",
                    "1",
                  );
                }}
                className="p-1 rounded hover:bg-blue-100 text-blue-600"
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Provider results */}
        <div className="grid gap-4">
          {filteredProviders.map((provider) => (
            <Link
              to={
                patientId
                  ? `/provider/${provider.id}/patient/${patientId}`
                  : `/provider/${provider.id}`
              }
              key={provider.id}
              className="block"
            >
              <Card className="hover:shadow-lg cursor-pointer transition-shadow">
                <CardContent className="p-5">
                  <div className="flex gap-4 items-start">
                    {/* Profile photo */}
                    <div className="flex-shrink-0">
                      {provider.profilePictureUrl ? (
                        <img
                          src={provider.profilePictureUrl}
                          alt={`Dr. ${toTitleCase(provider.name)}`}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                      ) : (
                        <UserCircle size={64} className="text-gray-300" />
                      )}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">
                            Dr. {toTitleCase(provider.name)}
                          </h3>
                          <p className="text-teal-600 font-medium text-sm flex items-center gap-1 mt-0.5">
                            <Stethoscope size={13} />
                            {provider.specialty}
                          </p>
                        </div>

                        {/* Rating — extreme right */}
                        {(() => {
                          const r =
                            provider.averageRating ??
                            ratingsMap[provider.id]?.averageRating;
                          const rc =
                            provider.reviewCount ??
                            ratingsMap[provider.id]?.reviewCount;
                          return r != null ? (
                            <div className="flex items-center gap-1 text-sm flex-shrink-0 ml-4">
                              <span className="text-yellow-500">★</span>
                              <span className="text-gray-700 font-semibold">
                                {Number(r).toFixed(1)}
                              </span>
                              {rc != null ? (
                                <span className="text-gray-400 text-xs">
                                  ({rc} {rc === 1 ? "review" : "reviews"})
                                </span>
                              ) : null}
                            </div>
                          ) : null;
                        })()}
                      </div>

                      {/* Details */}
                      <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                        {provider.distanceMiles != null && (
                          <span className="flex items-center gap-1 text-teal-600 font-medium">
                            <MapPin size={13} className="flex-shrink-0" />
                            {provider.distanceMiles.toFixed(1)} miles away
                          </span>
                        )}
                        {provider.location && provider.location !== "N/A" && (
                          <span className="flex items-center gap-1">
                            <MapPin size={13} className="flex-shrink-0" />
                            {provider.location}
                          </span>
                        )}
                        {provider.languages && provider.languages !== "N/A" && (
                          <span className="flex items-center gap-1">
                            <Globe size={13} className="flex-shrink-0" />
                            {provider.languages}
                          </span>
                        )}
                        {provider.gender && provider.gender !== "N/A" && (
                          <span className="flex items-center gap-1">
                            <User size={13} className="flex-shrink-0" />
                            {provider.gender}
                          </span>
                        )}
                        {provider.yearsExperience && (
                          <span className="flex items-center gap-1">
                            <Clock size={13} className="flex-shrink-0" />
                            {provider.yearsExperience === "N/A"
                              ? "N/A"
                              : `${provider.yearsExperience} yrs experience`}
                          </span>
                        )}
                      </div>

                      {/* Accepting new patients badge */}
                      {provider.acceptingNewPatients === true && (
                        <div className="mt-2">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                            <CheckCircle size={12} />
                            Accepting New Patients
                          </span>
                        </div>
                      )}

                      {/* Availability */}
                      <div className="mt-2">{renderAvailability(provider)}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
          {filteredProviders.length === 0 && (
            <p className="text-gray-500 italic">
              No providers found.{" "}
              <Link
                to="/CreateProviderAlert"
                className="text-blue-600 underline hover:text-blue-800"
                title="Create Alert"
              >
                Create an alert
              </Link>{" "}
              to get notified when a provider is available.
            </p>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-4 py-6">
              <button
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage === 0}
                className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Previous page"
              >
                ‹
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage >= totalPages - 1}
                className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                aria-label="Next page"
              >
                ›
              </button>
            </div>
          )}
        </div>
      </div>

      {/* 🔵 Auth Modal */}
      <AuthModal
        open={showAuthModal}
        onClose={closeAuthModal}
        onSignIn={handleSignIn}
        onSignUp={openRoleModal}
      />

      <RoleModal
        open={showRoleModal}
        onClose={closeRoleModal}
        onSelectRole={handleSelectRole}
      />

      <NpiSearchModal
        open={showNpiModal}
        onClose={() => setShowNpiModal(false)}
        onFound={handleFoundByNpi}
      />

      <ProviderPreviewModal
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        onBack={handleBackToNpi}
        preview={providerPreview}
        npi={pendingNpi}
        onContinue={() => {
          setShowPreviewModal(false);
          setShowVerificationModal(true);
        }}
      />

      <ProviderVerificationDocumentModal
        open={showVerificationModal}
        npi={pendingNpi}
        onClose={() => setShowVerificationModal(false)}
        onBack={() => {
          setShowVerificationModal(false);
          setShowPreviewModal(true);
        }}
        onUploaded={async () => {
          setShowVerificationModal(false);

          if (!pendingNpi) {
            showToast("Missing NPI. Please search again.", "error");
            return;
          }

          localStorage.setItem(PENDING_CLAIM_KEY, String(pendingNpi));

          if (keycloak.authenticated && loggedInEmail) {
            try {
              await claimProviderByNpi(pendingNpi, { email: loggedInEmail });
              localStorage.removeItem(PENDING_CLAIM_KEY);
              await refreshUser();
            } catch (err) {
              console.error("Failed to claim provider profile:", err);
              showToast(
                "Unable to claim this profile. Please try again.",
                "error",
              );
            }
            return;
          }

          try {
            localStorage.setItem(LOCKED_KEY, "PROVIDER");
          } catch {}

          keycloak.register({ redirectUri: window.location.origin });
        }}
      />

      {/* Inline Booking Modal */}
      <BookingModal
        open={!!bookingSlot}
        onClose={() => setBookingSlot(null)}
        providerId={bookingSlot?.providerId}
        providerName={bookingSlot?.providerName || ""}
        slotZdt={bookingSlot?.zdt || ""}
        specialty={bookingSlot?.specialty || ""}
        facility={bookingSlot?.facility || ""}
      />

      {/* Advanced Filters Modal */}
      <AdvancedFiltersModal
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        stagedFilters={stagedFilters}
        setStagedFilters={setStagedFilters}
        onApply={handleApplyFilters}
        onReset={handleResetFilters}
        specialties={specialties}
        insurances={insurances}
        stateOptions={stateOptions}
        cityOptions={cityOptions}
        selectedStateId={selectedStateId}
        setSelectedStateId={setSelectedStateId}
        selectedCityId={selectedCityId}
        setSelectedCityId={setSelectedCityId}
        loadingStates={loadingStates}
        loadingCities={loadingCities}
        minYearsOfExperience={minYearsOfExperience}
        setMinYearsOfExperience={setMinYearsOfExperience}
        maxYearsOfExperience={maxYearsOfExperience}
        setMaxYearsOfExperience={setMaxYearsOfExperience}
        isAvailBetweenOpen={isAvailBetweenOpen}
        setIsAvailBetweenOpen={setIsAvailBetweenOpen}
      />
    </>
  );
}

export default ProviderSearchPage;
